import { RefObject, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { type VitalSigns } from '@binah/web-sdk';
import { useTranslation } from 'react-i18next';

import Icon from 'components/icon/Icon';
import { Progress } from 'components/ui/progress';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

import Mask from 'assets/mask.svg';
import useFaceScanStore from 'store/face-scan';
import { InfoData } from '../../../types';

interface ScanProps {
  info: InfoData;
  videoRef: RefObject<HTMLVideoElement>;
  vitalSigns: VitalSigns | null;
  isMeasuring: () => boolean;
  onMeasure: () => Promise<void>;
}

export default function Scan({
  info,
  videoRef,
  vitalSigns,
  isMeasuring,
  onMeasure,
}: ScanProps) {
  const { t } = useTranslation();

  const sdkConfig = useFaceScanStore((store) => store.sdkConfig);
  const processingTime = sdkConfig?.scan_duration || 0;

  const [progress, setProgress] = useState<number>(0);
  const [scannedTime, setScannedTime] = useState<number>(0);
  const [remainingTime, setRemainingTime] = useState<number>(processingTime);

  useEffect(() => {
    if (isMeasuring()) {
      const timer = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            clearInterval(timer);
            return 100;
          }
          return prevProgress + 100 / processingTime;
        });
        setRemainingTime((time) => time - 1);
        setScannedTime((time) => time + 1);
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setProgress(0);
    }
  }, [isMeasuring]);

  const CHECKS = [
    t('check_the_lighting'),
    t('donot_keep_face_far'),
    t('clear_camera_lens'),
  ];

  return (
    <div className='flex items-center justify-center h-full max-w-6xl mx-6 my-4'>
      <div
        className={cn(
          isMeasuring()
            ? 'flex flex-col items-center justify-center space-y-4 w-full'
            : 'space-y-4 w-full'
        )}
      >
        <div className='relative w-full h-full rounded-3xl'>
          <div className='relative h-[calc(100vh-18rem)]'>
            <img
              src={Mask}
              alt='mask'
              className='absolute top-0 left-0 z-10 object-cover w-full h-full'
            />
            <video
              ref={videoRef}
              id='video'
              muted
              playsInline
              className='absolute top-0 left-0 w-full h-full scale-x-[-1] transform object-cover'
            />
          </div>
        </div>

        {isMeasuring() ? (
          <div className='mx-6 space-y-4'>
            <div>
              {scannedTime < 10 ? (
                <p className='font-medium text-center'>
                  {t('scanning_your_face')}
                </p>
              ) : info?.message ? (
                <p className='font-medium text-center text-red-500'>
                  {info.message}
                </p>
              ) : (
                <p className='font-medium text-center'>
                  {remainingTime} {t('secs_remaining')}
                </p>
              )}

              <Progress value={progress} className='w-full' />
            </div>

            <div className='flex items-center gap-x-6'>
              <div className='flex items-center gap-2'>
                <Icon icon='breathing_rate' size={24} />

                <div className='font-medium leading-none'>
                  <h2>{t('breathing_rate')}</h2>

                  <p className='font-semibold'>
                    {vitalSigns?.respirationRate.isEnabled
                      ? `${vitalSigns.respirationRate.value || '--'} bpm`
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className='flex items-center gap-2'>
                <Icon icon='heart_rate' size={24} />

                <div className='font-medium leading-none'>
                  <h2>{t('heart_rate')}</h2>

                  <p className='font-semibold'>
                    {vitalSigns?.pulseRate.isEnabled
                      ? `${vitalSigns.pulseRate.value || '--'} bpm`
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className='flex justify-center'>
              <Button onClick={onMeasure} className='px-8'>
                {t('stop')}
              </Button>
            </div>
          </div>
        ) : (
          <div className='flex flex-col justify-center max-w-md mx-auto space-y-6 max-sm:mt-4'>
            <div className='space-y-2 font-medium'>
              {CHECKS.map((check) => (
                <div
                  key={check}
                  className='flex items-center justify-between gap-4'
                >
                  <p className='text-sm leading-tight sm:text-base'>{check}</p>
                  <div className='border-[2px] flex justify-center items-center rounded-sm border-primary '>
                    <Check className='w-3 h-3 stroke-2 shrink-0 text-primary' />
                  </div>
                </div>
              ))}
            </div>

            <div className='flex justify-center'>
              <Button onClick={onMeasure}>{t('start_scan')}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
